/* eslint-disable no-restricted-globals */

import {
  healthPaymentResultsLayer,
  hrtPaymentResultsLayer,
  landingLayer,
  mainSurveyLayer,
  paymentCheckoutLayer,
  paymentSurveyLayer,
} from "@pageDefinitions/goto/constants";
import { captureException } from "src/utils/error";
import goto from ".";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
  isInsideNoomWebView,
  isInsidePartnerWebView,
  pageSetPushReroll,
  setParam,
} from "./utils";
import {
  getAndroidMessageHandlers,
  getIOSMessageHandlers,
} from "src/utils/in-app/messageHandlers";
import {
  getHmRouteId,
  getMeristemContext,
  getRouteId,
} from "@utils/meristemContext";
import plans from "@utils/plansMapping";
import {
  isB2BUpsellClinicalRoute,
  isFreemiumUpsellRoute,
  isHM,
} from "src/utils/userSegment";
import {
  updateForwardedParams,
  updateURLParams,
  getURLParams,
} from "src/utils/urlParams";
import { reroll } from "../components/PageSetRouter";
import { routeConstants } from "src/utils/constants";
import { getSessionState } from "@pageDefinitions/session";
import { init as clearPiiParams } from "@utils/urlParamsPii";
import { buildDeepLink } from "src/utils/in-app/deeplinkHandler";
import type { NativePartnerInteropMessageProto } from "@noom/noom-contracts/noom_contracts/mobile/webview/partner/interop";
import { logDebug } from "src/utils/monitoring/logging";
import { isDevMode } from "src/utils/userSegment/features";

export function isBuyflow() {
  return ["landing", "main-survey"].includes(getMeristemContext().context_type);
}

export const linkTo = {
  mainSurvey() {
    return "/ps/main-survey";
  },
};

const routeSwitch = (currentRoute: string, newRoute: string) => {
  if (currentRoute !== newRoute) {
    // Ensure that if we come back, the route is preserved
    updateURLParams({ route: currentRoute });
    updateForwardedParams({ route: newRoute });
  }
};

const hmRouteSwitch = (
  currentRoute: string = getRouteId(),
  newRoute: string = getHmRouteId()
) => {
  routeSwitch(currentRoute, newRoute);
};

export default {
  // Landing
  landing(replaceHistory = false, routeId?: string, entryPoint?: string) {
    goto.layer(landingLayer, null, replaceHistory, routeId, entryPoint);
  },
  hmLanding() {
    hmRouteSwitch();
    reroll(`/programs/mood/${getHmRouteId()}`);
  },
  directHome() {
    clearPiiParams();

    const params = new URLSearchParams(window.location.search);
    setParam(params, "route", routeConstants.direct);

    window.location.href = `/?${params}`;
  },
  async directLanding(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.landing(false, routeConstants.direct, entryPoint);
  },
  async clinicalLanding(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.landing(false, routeConstants.clinical, entryPoint);
  },

  // Survey
  async mainSurvey(
    replaceHistory = false,
    routeId?: string,
    entryPoint?: string
  ) {
    return goto.layer(
      mainSurveyLayer,
      null,
      replaceHistory,
      routeId,
      entryPoint
    );
  },

  async switchToMedMainSurvey() {
    updateURLParams({ route: getRouteId() });
    const destinationRoute = isFreemiumUpsellRoute()
      ? routeConstants.freemiumClinical
      : routeConstants.clinical;
    goto.mainSurvey(false, destinationRoute, "continueWithBuildingMed");
  },

  // Survey
  async paymentSurvey(
    replaceHistory = false,
    routeId?: string,
    entryPoint?: string
  ) {
    return goto.layer(
      paymentSurveyLayer,
      null,
      replaceHistory,
      routeId,
      entryPoint
    );
  },

  async hrtPaymentResultsSurvey(replaceHistory = false, routeId?: string) {
    return goto.layer(hrtPaymentResultsLayer, null, replaceHistory, routeId);
  },

  async healthPaymentResultsSurvey(replaceHistory = false, routeId?: string) {
    return goto.layer(healthPaymentResultsLayer, null, replaceHistory, routeId);
  },

  // NW payment checkout
  async paymentCheckout(noomPlanId: string) {
    updateURLParams({
      noom_plan_id: noomPlanId,
    });
    updateForwardedParams({
      noom_plan_id: plans["3-7-DAY-TRIAL"],
    });

    routeSwitch(getRouteId(), routeConstants.direct);
    return goto.layer(paymentCheckoutLayer);
  },

  // NW payment checkout
  async paymentCheckoutInitialRoute(noomPlanId: string) {
    const { initialRouteId } = getSessionState("browser");
    updateURLParams({
      noom_plan_id: noomPlanId,
    });

    routeSwitch(getRouteId(), initialRouteId || routeConstants.direct);
    return goto.layer(paymentCheckoutLayer);
  },

  // HM Survey
  async hmMainSurvey() {
    hmRouteSwitch();
    return goto.mainSurvey();
  },

  async hwDirectMainSurvey(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.mainSurvey(false, routeConstants.direct, entryPoint);
  },

  async exitClinicalUpsellSurvey(entryPoint?: string) {
    if (isB2BUpsellClinicalRoute()) {
      return goto.exitBuyflow("LANDING");
    }

    updateURLParams({ route: getRouteId() });
    return goto.mainSurvey(false, routeConstants.freemiumWeight, entryPoint);
  },

  async hwDirectPaymentSurvey() {
    updateURLParams({ route: getRouteId() });
    return goto.paymentSurvey(false, routeConstants.direct);
  },

  async hwPaymentSurveyInitialRoute(entryPoint?: string) {
    const { initialRouteId } = getSessionState("browser");
    updateURLParams({ route: getRouteId() });
    return goto.paymentSurvey(
      false,
      initialRouteId || routeConstants.direct,
      entryPoint
    );
  },

  async hwFreemiumPaymentSurvey(entryPoint?: string) {
    updateURLParams({ route: getRouteId() });
    return goto.paymentSurvey(false, routeConstants.freemiumWeight, entryPoint);
  },

  async hwPaymentCheckout() {
    updateURLParams({ route: getRouteId() });
    routeSwitch(getRouteId(), routeConstants.direct);
    return goto.layer(paymentCheckoutLayer);
  },

  /**
   * This is intended mostly as a stopgap and may not work for all future implementations.
   * @deprecated Use page set navigation instead.
   */
  async surveyQuestion(question: string) {
    if (!question) {
      goto.mainSurvey();
      return;
    }

    pageSetPushReroll(
      isHM() ? `/survey/hm/${question}` : `/survey/${question}`
    );
  },

  async choosePlan() {
    pageSetPushReroll(isHM() ? "/survey/hm/choosePlan" : "/survey/choosePlan");
  },

  /**
   * Action for exiting the buyflow. If the buyflow is opened in a webview, we will try to close it by using message handlers.
   * Otherwise, we will use the native fallback.
   *
   * @param nativeFallback - The fallback to use if the native buyflow exit fails.
   * @returns true if the buyflow was exited successfully, false otherwise.
   */
  exitBuyflow(nativeFallback?: "LANDING" | "USERPORTAL") {
    logDebug("exitBuyflow", nativeFallback);
    if (isInsidePartnerWebView()) {
      return goto.exitPartnerWebView();
    }
    if (isInsideIOSWebKit()) {
      logDebug("exitBuyflow ios webkit");
      try {
        const messageHandlers = getIOSMessageHandlers();
        if (messageHandlers?.buyflowExit) {
          messageHandlers.buyflowExit.postMessage({});
          return true;
        }
        if (messageHandlers?.webviewExit) {
          messageHandlers.webviewExit.postMessage({});
          return true;
        }
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "exitBuyflow.ios");
      }
    }
    if (isInsideAndroidWebView()) {
      logDebug("exitBuyflow android webview");
      try {
        const messageHandlers = getAndroidMessageHandlers();
        if (messageHandlers?.buyflowExit) {
          messageHandlers.buyflowExit();
          return true;
        }
        if (messageHandlers?.webviewExit) {
          messageHandlers.webviewExit();
          return true;
        }
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "exitBuyflow.android");
      }
    }

    if (nativeFallback === "LANDING") {
      logDebug("exitBuyflow landing");
      return goto.directHome();
    }

    if (nativeFallback === "USERPORTAL") {
      logDebug("exitBuyflow userportal");
      const addCloseParam = isInsideNoomWebView();
      return goto.userPortalHomePage(addCloseParam);
    }
    logDebug("exitBuyflow done");
    return false;
  },

  exitPartnerWebView() {
    if (!isInsidePartnerWebView()) {
      captureException(
        new Error("exitPartnerWebView: not inside partner webview"),
        "exitPartnerWebView"
      );
      return false;
    }

    const message: NativePartnerInteropMessageProto = {
      messageId: crypto.randomUUID(),
      action: {
        exitWebview: {},
      },
    };
    if (isInsideAndroidWebView()) {
      try {
        const messageHandlers = getAndroidMessageHandlers();
        if (messageHandlers?.sendNativePartnerInteropMessage) {
          messageHandlers.sendNativePartnerInteropMessage(
            JSON.stringify(message)
          );
          return true;
        }
      } catch (err) {
        captureException(err, "exitPartnerWebView.android");
      }
    }

    if (isInsideIOSWebKit()) {
      try {
        const messageHandlers = getIOSMessageHandlers();
        if (messageHandlers?.sendNativePartnerInteropMessage) {
          messageHandlers.sendNativePartnerInteropMessage.postMessage(
            JSON.stringify(message)
          );
          return true;
        }
      } catch (err) {
        captureException(err, "exitPartnerWebView.ios");
      }
    }

    captureException(
      new Error("exitPartnerWebView: no message handler found"),
      "exitPartnerWebView"
    );
    return false;
  },

  closeInAppContentScreen() {
    // score=1 will mark the in-app task as complete
    window.location.href = "https://unknown.noom.com?score=1&close=1";
  },
  closeInAppWebview() {
    window.location.href = "https://unknown.noom.com?close=1";
  },

  handleFreemiumComplete() {
    if (isInsideAndroidWebView()) {
      try {
        const messageHandler = getAndroidMessageHandlers();
        if (messageHandler?.buyflowPurchaseCompleted) {
          messageHandler.buyflowPurchaseCompleted();
          return true;
        }
      } catch (err) {
        captureException(err, "handleFreemiumComplete.android");
      }
    } else if (isInsideIOSWebKit()) {
      try {
        const messageHandler = getIOSMessageHandlers();
        if (messageHandler?.freemiumBuyflowComplete) {
          messageHandler.freemiumBuyflowComplete.postMessage({});
          return true;
        }
      } catch (err) {
        captureException(err, "handleFreemiumComplete.ios");
      }
    }

    return false;
  },

  deeplink(params: {
    open: string;
    close: string;
    forwardParams?: {
      originalParam: string;
      deeplinkParam: string;
    }[];
    extraParams?: object;
  }) {
    const { open, close, forwardParams, extraParams } = params;
    const allParams = { open, close };
    if (forwardParams) {
      const existingParams = getURLParams();
      forwardParams.forEach(({ originalParam, deeplinkParam }) => {
        if (existingParams[originalParam]) {
          // just ignore if the original param does not exist
          allParams[deeplinkParam] = existingParams[originalParam];
        }
      });
    }
    if (extraParams) {
      Object.entries(extraParams).forEach(([key, value]) => {
        allParams[key] = value;
      });
    }
    const useNativeDeepLink = !isInsideAndroidWebView();
    window.location.href = buildDeepLink(allParams, useNativeDeepLink);
  },

  /**
   * Opens the given URL in an external browser.
   * At the moment, only available inside the iOS webview.
   */
  openExternalBrowser(url: string) {
    try {
      const iosHandlers = getIOSMessageHandlers();
      if (iosHandlers?.openExternalBrowser) {
        iosHandlers.openExternalBrowser.postMessage(url);
      } else if (isDevMode()) {
        navigator.clipboard.writeText(url);
        // eslint-disable-next-line no-alert
        alert(
          "The URL has been copied to your clipboard. Please open it in a different browser."
        );
      } else {
        window.open(url, "_blank");
      }
    } catch {
      window.location.href = url;
    }
  },
};
